import { FC } from 'react';
import { clx, config, formatLocalAddress } from 'Utils';
import { useModal, useResponsive } from 'Hooks';
import {
	Address,
	Media,
	Tab,
	XOR,
	BusinessPartnerMe,
	CommunityPartnerMe
} from 'Types';
import {
	Card,
	TabNavigation,
	ShimmerImage,
	ModalBoxChangeImage,
	ProfileDecorationBusinessPartnerBadge,
	FontAwesome,
	ProfileDecorationCommunityPartnerBadge,
	Button
} from 'Elements';
import pageStyles from 'partials/shared/page.module.scss';
import styles from 'partials/decoration/profile-decoration-box.module.scss';
import { useTranslation } from 'react-i18next';
import { SkeletonProfileDecorationBox } from './SkeletonProfileDecorationBox';

interface SharedProps {
	logo: string;
	thumbnailLogo: string;
	address: Address;
	isLoading: boolean;
	title: string;
	tabs: Tab[];
	onResetLogo: () => void;
	onChangeLogo: (image_data: Media) => void;
	refetchPartnerProfile?: () => void;
}
type CommunityPartner = {
	type: 'communityPartner';
	communityPartnerMe?: CommunityPartnerMe;
	profile: CommunityPartnerMe;
};
type BusinessPartner = {
	type: 'businessPartner';
	businessPartnerMe?: BusinessPartnerMe;
	profile: BusinessPartnerMe;
};
type Props = SharedProps & XOR<CommunityPartner, BusinessPartner>;

export const ProfileDecorationBox: FC<Props> = ({
	isLoading,
	logo,
	thumbnailLogo,
	address,
	title,
	tabs,
	type,
	profile,
	onResetLogo,
	onChangeLogo
}): JSX.Element => {
	const { t } = useTranslation();
	const [openModalLogo, toggleModalLogo] = useModal();
	const { isTabletAndAbove, isDesktopAndAbove } = useResponsive();
	const defaultImage = '/assets/img/business-partner-default.png';
	const getImageSize = () => {
		return isDesktopAndAbove ? 160 : isTabletAndAbove ? 136 : 112;
	};
	const previewUrl = `${config.publicUrl}/${
		type === 'communityPartner' ? 'community-partner' : 'business-partner'
	}/${profile?.id}/${profile?.slugUrl}`;

	const onPreviewPage = () => {
		window.open(previewUrl, '_blank', 'noopener,noreferrer');
	};
	const webSite =
		type === 'communityPartner'
			? profile?.webSite
			: profile?.webSiteAddress;
	return (
		<Card tab>
			{isLoading ? (
				<SkeletonProfileDecorationBox />
			) : (
				<div
					className={clx(
						styles.body,
						'd-flex flex-column flex-md-row gap-3'
					)}>
					<div
						role="button"
						tabIndex={-1}
						className={styles.logo}
						onClick={toggleModalLogo}>
						<div className={styles.logoBox}>
							<ShimmerImage
								width={getImageSize()}
								height={getImageSize()}
								src={thumbnailLogo ?? defaultImage}
								defaultImage={defaultImage}
								className={styles.shimmer}
								alt={title ?? ''}
							/>
						</div>
						<span className={styles.edit}>
							<FontAwesome icon="pen" size="xl" />
						</span>
					</div>
					<div className="flex-grow-1">
						<>
							<h3 className="h5 lh-sm fw-400 text-break">
								{title}
							</h3>
							<div className="d-flex align-items-center flex-wrap gap-3 mb-3">
								{type === 'communityPartner' && (
									<ProfileDecorationCommunityPartnerBadge
										profile={profile}
									/>
								)}
								{type === 'businessPartner' &&
									profile?.publishInfo && (
										<ProfileDecorationBusinessPartnerBadge
											profile={profile}
										/>
									)}
							</div>
							{address && (
								<div className="text-gray-3 fw-300 fs-sm d-flex lh-base mb-2">
									<FontAwesome
										icon="location-dot"
										size="1x"
										className="me-2"
									/>
									{formatLocalAddress(
										address?.street,
										address?.houseNumber,
										address?.postalCode,
										address?.city,
										address?.state
									)}
								</div>
							)}
							<div className="mb-2">
								<span className="text-gray-3 fw-300 fs-sm d-flex lh-base">{`${t(
									'card.region-code'
								)}: ${profile?.regionCode}XXX`}</span>
							</div>
							<div className="mb-2">
								{webSite && (
									<a
										href={webSite}
										target="_blank"
										rel="noreferrer"
										className="fw-300 text-gray-3">
										{webSite}
									</a>
								)}
							</div>

							<Button
								as="a"
								to={
									type === 'communityPartner'
										? '/my-community-partner/homepage'
										: '/my-business-partner/homepage'
								}
								target="_blank"
								className="flex-fill gap-2 ">
								<FontAwesome icon="pen-to-square" size="lg" />
								{t('button.edit-public-page')}
							</Button>
						</>
					</div>
					<div>
						{profile?.publishInfo?.status === 1 &&
							profile?.publishInfo?.version && (
								<Button
									disabled={isLoading}
									onClick={onPreviewPage}
									className="flex-fill">
									<FontAwesome
										icon="eye"
										className="me-2"
										size="lg"
									/>
									{t('button.view-public-page')}
								</Button>
							)}
					</div>
				</div>
			)}
			<div className={pageStyles.tab}>
				{tabs?.map((tab) => {
					return (
						<TabNavigation
							key={tab.id}
							to={tab.path}
							title={tab.title}
							isActive={tab.isActive}
						/>
					);
				})}
			</div>
			<ModalBoxChangeImage
				name="logo"
				isOpen={openModalLogo}
				title="title.change-logo"
				image={logo}
				defaultImage={defaultImage}
				onRemove={onResetLogo}
				onClose={toggleModalLogo}
				onImageChange={onChangeLogo}
			/>
		</Card>
	);
};
